import React, { useEffect, useState, useCallback } from "react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArchiveToggle } from "@/components/ArchiveToggle";
import { labelFor, IconFor, statusCls } from "@/lib/categories";
import { useLang } from "@/context/LanguageContext";
import { useEstablishment } from "@/context/EstablishmentContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RejectReasonModal } from "@/components/Verification";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import ShipmentRow from "@/components/ShipmentRow";
import { CarrierSelectPanel } from "@/components/CarrierSelectPanel";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import Pagination from "@/components/Pagination";
import { Bell, Mail, Phone, Calendar, MapPin, Sparkles, RefreshCw, Check, ScanEye, X, ZoomIn, ArrowRight, SlidersHorizontal, Truck, Home, CreditCard, Trash2, ShieldCheck, PackageCheck, AlertTriangle, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";


function VerificationClaimsPanel({ onDecided }) {
  const { t } = useLang();
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const reload = useCallback(() => {
    api("/claim/verification-claims").then((r) => setClaims(Array.isArray(r) ? r : [])).catch(() => setClaims([]));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useAutoRefresh(reload);
  async function decide(id, decision, reason = "") {
    setBusy(id + decision);
    try {
      await api(`/claim/verification-claims/${id}/${decision}`, { method: "POST", body: { reason } });
      toast.success(decision === "approve" ? t("hc.claimApproved2") : t("hc.claimRejected2"));
      setRejectId(null);
      reload();
      onDecided?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }
  if (!claims.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="verification-claims-panel">
      <p className="font-display font-bold text-findo-navy flex items-center gap-1.5"><ShieldCheck size={18} /> {t("hc.claimsToValidate")} ({claims.length})</p>
      <p className="text-xs text-slate-600 mt-0.5 mb-3">{t("hc.aUserClaimsToBe")}</p>
      <div className="space-y-3">
        {claims.map((c) => (
          <div key={c.id} className="rounded-xl border border-amber-200 bg-white p-3.5" data-testid={`verification-claim-${c.id}`}>
            <p className="text-sm font-semibold text-findo-navy">{c.claimant_name || t("hc.claimant")} — « {(c.ad_description || "").slice(0, 60) || t("hc.item")} »</p>
            {Array.isArray(c.verification_questions) && c.verification_questions.length > 0 && (
              <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2" data-testid={`claim-deposit-questions-${c.id}`}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-700 flex items-center gap-1.5">
                  <HelpCircle size={12} /> {t("hc.yourVerificationQuestionsDeposit")}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{t("hc.compareThemWithTheOwner")}</p>
                <ol className="mt-1 space-y-0.5 list-decimal list-inside">
                  {c.verification_questions.map((q, i) => (
                    <li key={i} className="text-xs text-slate-700" data-testid={`claim-deposit-question-${c.id}-${i}`}>{q}</li>
                  ))}
                </ol>
              </div>
            )}
            <div className="mt-2 space-y-1.5">
              {(c.answers || []).map((a, i) => (
                <div key={i} className="text-sm">
                  <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{a.question || `${t("hc.characteristic")} ${i + 1}`}</span>
                  <p className="text-slate-700">{a.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => decide(c.id, "approve")} disabled={!!busy} className="rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid={`verification-approve-${c.id}`}>
                <Check size={14} className="mr-1" /> {busy === c.id + "approve" ? "…" : t("hc.approveTheClaim")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejectId(c.id)} disabled={!!busy} className="rounded-full font-semibold border-red-300 text-red-600 hover:bg-red-50" data-testid={`verification-reject-${c.id}`}>
                <X size={14} className="mr-1" /> {busy === c.id + "reject" ? "…" : t("hc.rejectTheClaim")}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <RejectReasonModal open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)} busy={!!busy}
        onConfirm={(reason) => decide(rejectId, "reject", reason)} />
    </div>
  );
}

function scoreColor(score) {
  const pct = Math.round((score || 0) * 100);
  if (pct >= 80) return "text-teal-700 bg-teal-100";
  if (pct >= 55) return "text-amber-700 bg-amber-100";
  return "text-slate-600 bg-slate-100";
}

const DONE_STATUSES = ["restituted", "delivered", "shipped", "closed", "resolved", "cancelled_by_customer"];

export default function Declarations() {
  const { t } = useLang();
  const { partner } = useAuth();
  const { scope, selectedId } = useEstablishment();
  const canBulk = ["owner", "admin"].includes(partner?.role || "owner");
  const [decls, setDecls] = useState(null);
  const [extPending, setExtPending] = useState(0);
  const [busy, setBusy] = useState(null);
  const [visual, setVisual] = useState({}); // declId -> [{item_id, score, reason, image_base64, description}]
  const [visBusy, setVisBusy] = useState(null);
  const [zoom, setZoom] = useState(null); // { src, label }
  const [compareMode, setCompareMode] = useState({}); // itemId -> bool (before/after slider)
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [verifyCodes, setVerifyCodes] = useState({}); // declId -> claim code input (inline verify)
  const [shipDecl, setShipDecl] = useState(null); // declaration whose shipment is being managed inline
  const [closeConfirm, setCloseConfirm] = useState(null); // declaration pending close confirmation
  const [shipItem, setShipItem] = useState(undefined); // undefined=loading, null=none, object=found
  const [meta, setMeta] = useState({ pages: 1, total: 0, active_total: 0, archived_total: 0, cancelled_total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedArchived, setExpandedArchived] = useState({});
  const toggleExpand = (id) => setExpandedArchived((m) => ({ ...m, [id]: !m[id] }));
  const PAGE_SIZE = 10;
  useEffect(() => { setPage(1); }, [selectedId, showArchived]);
  const cancelledCount = meta.cancelled_total || 0;

  async function archiveAllCancelled() {
    if (!window.confirm(t("hc.archiveCancelledConfirm").replace("{n}", cancelledCount))) return;
    setBusy("archiveAll");
    try {
      const r = await api(scope("/declarations/archive-cancelled"), { method: "POST" });
      toast.success(t("hc.nArchived").replace("{n}", r.archived));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  const load = useCallback(async () => {
    try {
      const r = await api(scope(`/declarations?page=${page}&page_size=${PAGE_SIZE}&archived=${showArchived}`));
      setDecls(r.declarations || []);
      setMeta({
        pages: r.pages || 1, total: r.total || 0,
        active_total: r.active_total || 0, archived_total: r.archived_total || 0,
        cancelled_total: r.cancelled_total || 0,
      });
      api("/declarations/external-pending-count").then((x) => setExtPending(x?.count || 0)).catch(() => {});
    } catch (e) { toast.error(e.message); }
  }, [scope, page, showArchived]);

  useEffect(() => {
    load();
    api("/declarations/mark-seen", { method: "POST" }).catch(() => {});
  }, [load, selectedId]);

  // Auto-refresh: reflect client actions (payment, cancellation, status change)
  // without a manual reload.
  useAutoRefresh(load);

  // Close the zoom lightbox on Escape.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Deep-link from the Shipments overview: ?shipment=<declaration_id> opens the
  // shipment management dialog directly for that declaration.
  useEffect(() => {
    const sid = searchParams.get("shipment");
    if (!sid) return;
    openShipment({ id: sid });
    const next = new URLSearchParams(searchParams);
    next.delete("shipment");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirmMatch(declaration_id, item_id) {
    setBusy(declaration_id + item_id);
    try {
      await api("/matching/confirm", { method: "POST", body: { declaration_id, item_id } });
      toast.success(t("dec.toastMatch"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }
  async function rerun(decl_id) {
    setBusy("rerun" + decl_id);
    try {
      await api(`/matching/rerun/${decl_id}`, { method: "POST" });
      toast.success(t("dec.toastRerun"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }
  const [confirmedExt, setConfirmedExt] = useState(new Set());
  async function confirmExternalPayment(decl_id, type) {
    setConfirmedExt((prev) => new Set(prev).add(decl_id));
    setBusy("ext" + decl_id);
    try {
      await api(`/declarations/${decl_id}/confirm-external-payment`, { method: "POST", body: { type } });
      toast.success(t("dec.toastExtPay"));
      window.dispatchEvent(new Event("findloop:ext-pending-changed"));
      load();
    } catch (e) {
      toast.error(e.message);
      setConfirmedExt((prev) => { const n = new Set(prev); n.delete(decl_id); return n; });
    } finally { setBusy(null); }
  }
  async function runVisual(d) {
    setVisBusy(d.id);
    try {
      const res = await api(`/vision/match/${d.id}`, { method: "POST", body: { image_base64: d.image_base64 } });
      const sugg = res.suggestions || [];
      setVisual((v) => ({ ...v, [d.id]: sugg }));
      toast.success(sugg.length ? t("dec.toastVisualN").replace("{n}", sugg.length) : t("dec.toastVisualNone"));
    } catch (e) { toast.error(e.message); } finally { setVisBusy(null); }
  }
  async function rejectSuggestion(declaration_id, item_id) {
    setBusy(declaration_id + item_id);
    try {
      await api("/matching/reject", { method: "POST", body: { declaration_id, item_id } });
      toast.success(t("dec.toastReject"));
      // Optimistically drop it from the local visual list.
      setVisual((v) => ({ ...v, [declaration_id]: (v[declaration_id] || []).filter((s) => s.item_id !== item_id) }));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function verifyClaim(decl_id, raw) {
    const c = (raw || "").trim().toUpperCase();
    if (!c) return;
    setBusy("verify" + decl_id);
    try {
      await api(`/claim/${decl_id}/verify`, { method: "POST", body: { code: c } });
      toast.success(t("hc.claimCodeVerified"));
      setVerifyCodes((m) => ({ ...m, [decl_id]: "" }));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function regenerateCode(decl_id) {
    setBusy("regen" + decl_id);
    try {
      await api(`/claim/${decl_id}/regenerate`, { method: "POST" });
      toast.success(t("hc.newCodeGeneratedAndSent"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function markRestituted(d) {
    if (!d.matched_item_id) return;
    setBusy("restit" + d.id);
    try {
      await api(`/items/${d.matched_item_id}/restitute`, { method: "POST" });
      toast.success(t("hc.itemMarkedAsReturned"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function closeShipping(d) {
    setBusy("close" + d.id);
    try {
      await api(`/declarations/${d.id}/close-shipping`, { method: "POST" });
      toast.success(t("hc.declarationClosed"));
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }

  async function openShipment(d) {
    setShipDecl(d);
    setShipItem(undefined);
    try {
      let list = await api(scope("/shipments/mine"));
      let found = list.find((x) => x.declaration_id === d.id);
      // Not auto-created yet → create it on demand (recovery already paid & confirmed).
      if (!found) {
        try {
          await api(`/declarations/${d.id}/ensure-shipment`, { method: "POST" });
          list = await api(scope("/shipments/mine"));
          found = list.find((x) => x.declaration_id === d.id);
        } catch (e) { toast.error(e.message); }
      }
      setShipItem(found || null);
      // Deep-link opens with just {id}; label the dialog from the shipment data.
      if (found && found.customer_name) {
        setShipDecl((prev) => (prev && !prev.customer_name ? { ...prev, customer_name: found.customer_name } : prev));
      }
    } catch (e) { toast.error(e.message); setShipItem(null); }
  }

  async function refreshShipment() {
    if (!shipDecl) return;
    await load();
    try {
      const list = await api(scope("/shipments/mine"));
      setShipItem(list.find((x) => x.declaration_id === shipDecl.id) || null);
    } catch { /* ignore */ }
  }

  // Visual suggestions to render for a declaration: prefer the freshly-run
  // result, else the persisted ones (enriched server-side with the item photo).
  function visualFor(d) {
    // Freshly-run vision results take priority; else the persisted visual
    // suggestions already enriched server-side with the item photo + description.
    return visual[d.id] || d.visual_suggestions || [];
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <PageHeader title={t("db.declarations.t")} subtitle={t("db.declarations.s")} testid="declarations-header" />

      <VerificationClaimsPanel onDecided={load} />

      {extPending > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-findo-indigo/25 bg-findo-indigo/5 px-4 py-3" data-testid="ext-pending-banner">
          <span className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-findo-indigo text-white"><CreditCard size={18} /></span>
          <p className="text-sm text-slate-700" data-testid="ext-pending-count-wrap">
            <b className="text-findo-indigo" data-testid="ext-pending-count">{extPending}</b>{" "}
            {t("dec.extBanner").replace("{n}", "").trimStart()}
          </p>
        </div>
      )}

      {decls !== null && (meta.active_total > 0 || meta.archived_total > 0) && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <ArchiveToggle showArchived={showArchived} onChange={setShowArchived}
            activeCount={meta.active_total} archivedCount={meta.archived_total} t={t} />
          {canBulk && cancelledCount > 0 && (
            <button onClick={archiveAllCancelled} disabled={busy === "archiveAll"}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-findo-red/30 bg-findo-red/5 text-findo-red px-3.5 h-9 text-sm font-semibold hover:bg-findo-red/10 transition-colors disabled:opacity-50"
              data-testid="archive-all-cancelled">
              <Trash2 size={14} /> {busy === "archiveAll" ? "…" : `Archiver toutes les annulations (${cancelledCount})`}
            </button>
          )}
        </div>
      )}

      {decls === null ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : decls.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl" data-testid="declarations-empty">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-indigo/10 text-findo-indigo"><Bell size={32} /></div>
          <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{showArchived ? t("hc.noArchivedDeclarations") : t("hc.noDeclarations")}</h3>
          <p className="mt-1 text-slate-500 text-sm">{showArchived ? t("hc.processedCasesWillAppearHere") : t("hc.shareYourQrCodeTo")}</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="declarations-list">
          {decls.map((d) => {
            const Icon = IconFor(d.category);
            const suggestions = d.suggestions || [];
            const visSugg = visualFor(d);
            const isArchived = showArchived || DONE_STATUSES.includes(d.status);  // archives = read-only
            return (
              <div key={d.id} className="bg-white rounded-2xl border border-border p-5 card-shadow" data-testid={`declaration-${d.id}`}>
                {!expandedArchived[d.id] ? (
                  <button type="button" onClick={() => toggleExpand(d.id)}
                    className="w-full flex items-center gap-3 text-left" data-testid={`declaration-compact-${d.id}`}>
                    <Icon size={18} className="text-findo-indigo shrink-0" />
                    <span className="font-semibold text-slate-800 text-sm shrink-0">{labelFor(d.category)}</span>
                    {d.status === "cancelled_by_customer"
                      ? <Badge className="bg-red-100 text-red-700">{t("hc.cancelled")}</Badge>
                      : <Badge className={statusCls(d.status)}>{t(`st.${d.status}`)}</Badge>}
                    <span className="ml-auto flex items-center gap-4 text-sm text-slate-500 min-w-0">
                      <span className="truncate max-w-[160px]" data-testid={`declaration-compact-client-${d.id}`}>{d.customer_name}</span>
                      <span className="flex items-center gap-1 shrink-0"><Calendar size={13} /> {d.lost_date}</span>
                      <ChevronDown size={16} className="text-slate-400 shrink-0" />
                    </span>
                  </button>
                ) : (
                <>
                <button type="button" onClick={() => toggleExpand(d.id)}
                  className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600" data-testid={`declaration-collapse-${d.id}`}>
                  <ChevronUp size={14} /> {t("hc.collapse")}
                </button>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-findo-indigo font-semibold text-sm">
                      <Icon size={16} /> {labelFor(d.category)}
                      {d.status === "cancelled_by_customer"
                        ? <Badge className="bg-red-100 text-red-700" data-testid={`declaration-cancelled-${d.id}`}>{t("hc.cancelledByTheCustomer")}</Badge>
                        : <Badge className={statusCls(d.status)}>{t(`st.${d.status}`)}</Badge>}
                    </div>
                    <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-slate-400" data-testid={`declaration-client-desc-label-${d.id}`}>{t("dec.clientDescription")}</p>
                    <p className="mt-0.5 text-slate-800">{d.description}</p>
                    {d.image_base64 && (
                      <button type="button" onClick={() => setZoom({ src: d.image_base64, label: t("dec.clientPhoto") })}
                        className="mt-3 block w-fit text-left group" data-testid={`declaration-photo-${d.id}`} title={t("dec.zoomPhoto")}>
                        <span className="relative inline-block">
                          <img src={d.image_base64} alt={t("dec.declaredItemPhoto")}
                            className="h-32 w-32 object-cover rounded-xl border border-border group-hover:opacity-90 transition-opacity" />
                          <span className="absolute bottom-1.5 right-1.5 h-6 w-6 grid place-items-center rounded-md bg-black/55 text-white"><ZoomIn size={14} /></span>
                        </span>
                        <span className="mt-1 block text-xs text-slate-400">{t("dec.clientPhoto")}</span>
                      </button>
                    )}
                    {d.image_base64_2 && (
                      <button type="button" onClick={() => setZoom({ src: d.image_base64_2, label: t("dec.clientPhoto") })}
                        className="mt-2 block w-fit text-left group" data-testid={`declaration-photo2-${d.id}`} title={t("dec.zoomPhoto")}>
                        <span className="relative inline-block">
                          <img src={d.image_base64_2} alt={t("dec.declaredItemPhoto")}
                            className="h-24 w-24 object-cover rounded-xl border border-border group-hover:opacity-90 transition-opacity" />
                          <span className="absolute bottom-1.5 right-1.5 h-6 w-6 grid place-items-center rounded-md bg-black/55 text-white"><ZoomIn size={14} /></span>
                        </span>
                        <span className="mt-1 block text-xs text-slate-400">{t("dec.clientPhoto")} 2</span>
                      </button>
                    )}
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5"><Calendar size={14} /> {d.lost_date}</span>
                      <span className="flex items-center gap-1.5"><MapPin size={14} /> {d.lost_place}</span>
                    </div>
                    <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm">
                      <p className="font-semibold text-slate-700">{d.customer_name}</p>
                      <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-slate-500">
                        <a href={`mailto:${d.customer_email}`} className="flex items-center gap-1.5 hover:text-findo-indigo"><Mail size={13} /> {d.customer_email}</a>
                        <a href={`tel:${d.customer_phone}`} className="flex items-center gap-1.5 hover:text-findo-indigo"><Phone size={13} /> {d.customer_phone}</a>
                      </div>
                      {d.delivery_method && (
                        <div className="mt-2 pt-2 border-t border-border" data-testid={`delivery-info-${d.id}`}>
                          {d.delivery_method === "shipping" ? (
                            <p className="font-semibold text-findo-indigo flex items-center gap-1.5"><Truck size={13} /> {t("dec.shippingMode")}</p>
                          ) : (
                            <p className="font-semibold text-emerald-700 flex items-center gap-1.5"><Home size={13} /> {t("dec.pickup")}</p>
                          )}
                        </div>
                      )}
                      {d.external_payment_mode === "external" && d.matched_item_id && (() => {
                        const isShip = d.delivery_method === "shipping";
                        // The partner always confirms reception of the RECOVERY fee
                        // (hybrid model: shipping is settled internally to findloop). So
                        // the persisted flag is external_recovery_confirmed in BOTH
                        // pickup and shipping orders → the button stays disabled after refresh.
                        const confirmed = d.external_recovery_confirmed;
                        return (
                          <div className="mt-2 pt-2 border-t border-border" data-testid={`ext-pay-${d.id}`}>
                            {confirmed ? (
                              <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
                                <Check size={14} /> {t("dec.payConfirmed").replace("{x}", isShip ? t("dec.pRecupShip") : t("dec.pRecup"))}
                              </p>
                            ) : (
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm text-slate-600 flex items-center gap-1.5">
                                  <CreditCard size={14} className="text-findo-indigo" /> {t("dec.payPending").replace("{x}", t("dec.pRecupP"))}
                                </p>
                                {!isArchived && (
                                  <Button size="sm" onClick={() => confirmExternalPayment(d.id, "recovery")}
                                    disabled={busy === "ext" + d.id || confirmedExt.has(d.id)}
                                    className="h-8 text-xs rounded-lg bg-findo-indigo hover:bg-findo-indigo/90"
                                    data-testid={`confirm-ext-payment-${d.id}`}>
                                    {(busy === "ext" + d.id || confirmedExt.has(d.id)) ? t("hc.confirmed") : t("c.confirm")}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {d.claim_code && (
                  <div className="mt-4 rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4" data-testid={`claim-security-${d.id}`}>
                    <p className="text-sm font-bold text-findo-indigo flex items-center gap-1.5"><ShieldCheck size={15} /> {t("hc.returnSecurity")}</p>
                    <p className="mt-1.5 text-xs text-slate-500">{t("hc.customerClaimCodeToCheck")}</p>
                    <p className="mt-1 text-xl font-bold tracking-[0.25em] text-findo-indigo font-mono bg-white rounded-lg border border-findo-indigo/20 py-2 text-center" data-testid={`claim-code-${d.id}`}>{d.claim_code}</p>
                    {d.claim_code_verified ? (
                      <p className="mt-1.5 text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid={`claim-verified-${d.id}`}><Check size={13} /> {t("hc.codeAlreadyVerified")}</p>
                    ) : (!isArchived && (
                      <div className="mt-2.5" data-testid={`claim-verify-block-${d.id}`}>
                        {d.claim_code_expired ? (
                          <div className="mb-2.5 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid={`claim-code-expired-${d.id}`}>
                            <span className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 flex-1">
                              <AlertTriangle size={14} /> {t("hc.claimCodeExpired")}
                            </span>
                            <Button size="sm" onClick={() => regenerateCode(d.id)} disabled={busy === "regen" + d.id}
                              className="h-8 rounded-lg shrink-0 bg-amber-600 hover:bg-amber-700 font-semibold" data-testid={`regenerate-code-btn-${d.id}`}>
                              <RefreshCw size={13} className="mr-1.5" /> {busy === "regen" + d.id ? "…" : t("hc.regenerateTheCode")}
                            </Button>
                          </div>
                        ) : null}
                        <div className="flex gap-2">
                          <Input value={verifyCodes[d.id] || ""} maxLength={16}
                            onChange={(e) => setVerifyCodes((m) => ({ ...m, [d.id]: e.target.value.toUpperCase() }))}
                            placeholder={t("hc.enterTheCustomerSCode")} className="h-9 rounded-lg font-mono uppercase text-sm"
                            data-testid={`verify-code-input-${d.id}`} />
                          <Button size="sm" onClick={() => verifyClaim(d.id, verifyCodes[d.id])}
                            disabled={busy === "verify" + d.id || !(verifyCodes[d.id] || "").trim()}
                            className="h-9 rounded-lg shrink-0" data-testid={`verify-code-btn-${d.id}`}>
                            {busy === "verify" + d.id ? "…" : t("hc.verify")}
                          </Button>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1">{t("hc.toCheckBeforeMarkingThe")}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700"><Sparkles size={15} className="text-findo-turquoise" /> {t("dec.suggestedMatches")}</span>
                    {!isArchived && (
                      <Button variant="ghost" size="sm" onClick={() => rerun(d.id)} disabled={busy === "rerun" + d.id} className="text-xs rounded-lg" data-testid={`rerun-${d.id}`}>
                        <RefreshCw size={13} className="mr-1" /> {t("dec.rerun")}
                      </Button>
                    )}
                  </div>
                  {suggestions.length === 0 ? (
                    <p className="text-sm text-slate-400">{t("dec.noMatchYet")}</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {suggestions.map((s) => {
                        const confirmed = d.matched_item_id === s.id;
                        return (
                          <div key={s.id} className={`rounded-xl border p-3 flex gap-3 ${confirmed ? "border-teal-400 bg-teal-50" : "border-border"}`} data-testid={`suggestion-${s.id}`}>
                            <div className="h-16 w-16 shrink-0 rounded-lg bg-slate-100 overflow-hidden">
                              {s.image_base64 && <img src={s.image_base64} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">{Math.round((s.score || 0) * 100)}%</span>
                              </div>
                              <p className="text-xs text-slate-600 line-clamp-2 mt-1">{s.description || s.reason}</p>
                              {confirmed ? (
                                <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-teal-700"><Check size={13} /> {t("c.confirmed")}</span>
                              ) : (!isArchived && (
                                <Button size="sm" onClick={() => confirmMatch(d.id, s.id)} disabled={busy === d.id + s.id}
                                  className="mt-1.5 h-7 text-xs rounded-lg" data-testid={`confirm-${d.id}-${s.id}`}>{t("c.confirm")}</Button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ---- AI visual comparison (photo vs photo) ---- */}
                {d.image_base64 && (
                  <div className="mt-4 border-t border-border pt-4" data-testid={`visual-section-${d.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
                        <ScanEye size={16} className="text-findo-indigo" /> {t("hc.aiVisualComparison")}
                      </span>
                      {!isArchived && (
                        <Button size="sm" onClick={() => runVisual(d)} disabled={visBusy === d.id}
                          className="h-8 text-xs rounded-lg bg-findo-indigo hover:bg-findo-indigo/90" data-testid={`visual-run-${d.id}`}>
                          {visBusy === d.id ? (<><FindoSpinner size={14} className="mr-1" /> {t("hc.analyzing")}</>)
                            : (<><ScanEye size={13} className="mr-1" /> {t("hc.comparePhotos")}</>)}
                        </Button>
                      )}
                    </div>

                    {visSugg.length === 0 ? (
                      <p className="text-sm text-slate-400" data-testid={`visual-empty-${d.id}`}>
                        {visBusy === d.id ? t("dec.visualRunning")
                          : t("dec.visualIdle")}
                      </p>
                    ) : (
                      <div className="space-y-3" data-testid={`visual-results-${d.id}`}>
                        {visSugg.map((s) => {
                          const itemImg = s.image_base64 || "";
                          const confirmed = d.matched_item_id === s.item_id;
                          return (
                            <div key={s.item_id}
                              className={`rounded-xl border p-3 ${confirmed ? "border-teal-400 bg-teal-50" : "border-findo-indigo/25 bg-findo-indigo/5"}`}
                              data-testid={`visual-suggestion-${s.item_id}`}>
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${scoreColor(s.score)}`} data-testid={`visual-score-${s.item_id}`}>
                                  {t("dec.similarity").replace("{n}", Math.round((s.score || 0) * 100))}
                                </span>
                                {confirmed ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700"><Check size={13} /> {t("c.confirmed")}</span>
                                ) : (!isArchived && (
                                  <div className="flex items-center gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => rejectSuggestion(d.id, s.item_id)} disabled={busy === d.id + s.item_id}
                                      className="h-7 text-xs rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`visual-reject-${d.id}-${s.item_id}`}>
                                      <X size={13} className="mr-1" /> {t("dec.reject")}
                                    </Button>
                                    <Button size="sm" onClick={() => confirmMatch(d.id, s.item_id)} disabled={busy === d.id + s.item_id}
                                      className="h-7 text-xs rounded-lg" data-testid={`visual-confirm-${d.id}-${s.item_id}`}>{t("c.confirm")}</Button>
                                  </div>
                                ))}
                              </div>
                              {/* side-by-side photos OR before/after slider */}
                              {compareMode[s.item_id] && itemImg ? (
                                <BeforeAfterSlider before={d.image_base64} after={itemImg} beforeLabel={t("dec.labelClient")} afterLabel={t("dec.labelRegistry")} />
                              ) : (
                                <div className="flex items-center gap-3">
                                  <figure className="flex-1 text-center">
                                    <button type="button" onClick={() => setZoom({ src: d.image_base64, label: t("dec.clientPhotoShort") })}
                                      className="relative block w-full group" title={t("dec.enlarge")}>
                                      <img src={d.image_base64} alt={t("dec.clientPhotoShort")}
                                        className="w-full h-28 object-cover rounded-lg border border-border group-hover:opacity-90 transition-opacity" />
                                      <span className="absolute bottom-1 right-1 h-5 w-5 grid place-items-center rounded bg-black/55 text-white"><ZoomIn size={12} /></span>
                                    </button>
                                    <figcaption className="mt-1 text-[11px] font-medium text-slate-500">{t("dec.clientPhotoShort")}</figcaption>
                                  </figure>
                                  <ArrowRight size={18} className="shrink-0 text-slate-400" />
                                  <figure className="flex-1 text-center">
                                    <button type="button" onClick={() => itemImg && setZoom({ src: itemImg, label: t("dec.foundItemRegistry") })}
                                      className="relative block w-full group" title={t("dec.enlarge")}>
                                      {itemImg ? (
                                        <img src={itemImg} alt={t("dec.foundItemAlt")}
                                          className="w-full h-28 object-cover rounded-lg border border-border group-hover:opacity-90 transition-opacity" />
                                      ) : (
                                        <div className="w-full h-28 grid place-items-center rounded-lg border border-dashed border-border text-slate-300 text-xs">{t("dec.noPhoto")}</div>
                                      )}
                                      {itemImg && <span className="absolute bottom-1 right-1 h-5 w-5 grid place-items-center rounded bg-black/55 text-white"><ZoomIn size={12} /></span>}
                                    </button>
                                    <figcaption className="mt-1 text-[11px] font-medium text-slate-500">{t("dec.foundItemRegistry")}</figcaption>
                                  </figure>
                                </div>
                              )}
                              {itemImg && (
                                <button type="button" onClick={() => setCompareMode((m) => ({ ...m, [s.item_id]: !m[s.item_id] }))}
                                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-findo-indigo hover:underline" data-testid={`before-after-toggle-${s.item_id}`}>
                                  <SlidersHorizontal size={13} /> {compareMode[s.item_id] ? t("dec.viewSideBySide") : t("dec.viewBeforeAfter")}
                                </button>
                              )}
                              {s.reason && (
                                <p className="mt-2 text-xs text-slate-600 italic" data-testid={`visual-reason-${s.item_id}`}>
                                  « {s.reason} »
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {d.matched_item_id && !DONE_STATUSES.includes(d.status) && !isArchived && (
                  <div className="mt-4 border-t border-border pt-3 flex items-center gap-2 flex-wrap">
                    {d.delivery_method !== "shipping" ? (() => {
                      const claimBlocks = d.claim_code && !d.claim_code_verified;
                      return (
                        <Button size="sm" onClick={() => markRestituted(d)}
                          disabled={busy === "restit" + d.id || claimBlocks}
                          title={claimBlocks ? t("hc.firstVerifyTheClaimCode") : ""}
                          className="h-8 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                          data-testid={`mark-restituted-${d.id}`}>
                          <PackageCheck size={14} className="mr-1" /> {busy === "restit" + d.id ? "…" : t("hc.markAsReturned")}
                        </Button>
                      );
                    })() : (
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <Button size="sm" onClick={() => openShipment(d)}
                          className="h-8 text-xs rounded-lg bg-findo-indigo hover:bg-findo-indigo/90 text-white"
                          data-testid={`open-shipment-${d.id}`}>
                          <Truck size={14} className="mr-1" /> {t("hc.createTheShipment")}
                        </Button>
                        {d.is_shipping_paid && (
                          <Button size="sm" onClick={() => setCloseConfirm(d)}
                            disabled={busy === "close" + d.id}
                            className="h-8 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                            data-testid={`close-shipping-${d.id}`}>
                            <PackageCheck size={14} className="mr-1" /> {busy === "close" + d.id ? "…" : t("hc.closeTheDeclaration")}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} pages={meta.pages} total={meta.total} size={PAGE_SIZE} onPage={setPage} testid="declarations-pagination" />

      {/* ---- Inline shipment management (shortcut from a declaration card) ---- */}
      <Dialog open={!!shipDecl} onOpenChange={(o) => { if (!o) { setShipDecl(null); setShipItem(undefined); } }}>
        <DialogContent className="max-w-2xl" data-testid="shipment-manage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck size={18} className="text-findo-indigo" /> {t("hc.shipping")} — {shipDecl?.customer_name}</DialogTitle>
            <DialogDescription>{t("hc.trackTheShipmentAndDownload")}</DialogDescription>
          </DialogHeader>
          {shipDecl?.id && <CarrierSelectPanel declId={shipDecl.id} />}
          {shipItem === undefined ? (
            <div className="py-10 grid place-items-center text-slate-400" data-testid="shipment-manage-loading">
              <FindoSpinner size={26} />
            </div>
          ) : shipItem === null ? (
            <div className="py-8 px-4 text-center rounded-xl bg-amber-50 border border-amber-200" data-testid="shipment-manage-none">
              <Truck size={28} className="mx-auto text-amber-500" />
              <p className="mt-2 text-sm font-semibold text-amber-800">{t("hc.noShipmentsYet2")}</p>
              <p className="mt-1 text-sm text-amber-700">
                {t("hc.theShipmentIsCreatedAutomatically")}
              </p>
            </div>
          ) : (
            <ShipmentRow s={shipItem} onSaved={refreshShipment} t={t} />
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Confirm before closing a shipping declaration ---- */}
      <AlertDialog open={!!closeConfirm} onOpenChange={(o) => { if (!o) setCloseConfirm(null); }}>
        <AlertDialogContent data-testid="close-shipping-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <PackageCheck size={18} className="text-emerald-600" /> {t("hc.closeTheDeclaration2")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("hc.theItem")} {closeConfirm?.customer_name ? <b>{t("hc.of")} {closeConfirm.customer_name}</b> : null} {t("hc.willBeMarkedAs")} <b>{t("hc.returned")}</b> {t("hc.andTheDeclarationWillBe")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="close-shipping-cancel">{t("hc.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const d = closeConfirm; setCloseConfirm(null); if (d) closeShipping(d); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="close-shipping-confirm">
              {t("hc.close2")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- Full-screen zoom lightbox ---- */}
      {zoom && (
        <div className="fixed inset-0 z-[1000] bg-black/85 backdrop-blur-sm grid place-items-center p-4 animate-in fade-in"
          onClick={() => setZoom(null)} data-testid="photo-lightbox">
          <button type="button" onClick={() => setZoom(null)}
            className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors"
            data-testid="lightbox-close" aria-label={t("c.close")}>
            <X size={22} />
          </button>
          <figure className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.src} alt={zoom.label} className="max-w-[92vw] max-h-[80vh] object-contain rounded-xl shadow-2xl" data-testid="lightbox-image" />
            {zoom.label && <figcaption className="mt-3 text-sm text-white/80">{zoom.label}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
}
